import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Award, Palette, Type, Loader2, Save, Eye, List, Plus, Trash2, Image, GripVertical, Bold, Italic, AlignLeft, AlignCenter, AlignRight, Copy, Upload } from 'lucide-react';
import { toast } from 'sonner';

const CANVAS_W = 800;
const CANVAS_H = 566; // A4 landscape ratio

/* ── Custom Draggable + Resizable box (replaces react-rnd) ──────────── */
function DraggableBox({ x, y, w, h, selected, onSelect, onDragStop, onResizeStop, enableResize, children, style: extraStyle }) {
  const boxRef = useRef(null);
  const dragging = useRef(null);   // { startX, startY, origX, origY }
  const resizing = useRef(null);   // { handle, startX, startY, origX, origY, origW, origH }

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  /* ─ drag ─ */
  const onPointerDownDrag = useCallback((e) => {
    if (e.target.dataset.handle) return; // resize handle, not drag
    e.stopPropagation();
    onSelect();
    dragging.current = { startX: e.clientX, startY: e.clientY, origX: x, origY: y };
    const onMove = (ev) => {
      if (!dragging.current) return;
      const dx = ev.clientX - dragging.current.startX;
      const dy = ev.clientY - dragging.current.startY;
      const nx = clamp(dragging.current.origX + dx, 0, CANVAS_W - w);
      const ny = clamp(dragging.current.origY + dy, 0, CANVAS_H - h);
      boxRef.current.style.left = nx + 'px';
      boxRef.current.style.top = ny + 'px';
    };
    const onUp = (ev) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      if (!dragging.current) return;
      const dx = ev.clientX - dragging.current.startX;
      const dy = ev.clientY - dragging.current.startY;
      dragging.current = null;
      onDragStop(clamp(x + dx, 0, CANVAS_W - w), clamp(y + dy, 0, CANVAS_H - h));
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [x, y, w, h, onSelect, onDragStop]);

  /* ─ resize ─ */
  const onPointerDownResize = useCallback((e, handle) => {
    e.stopPropagation();
    e.preventDefault();
    resizing.current = { handle, startX: e.clientX, startY: e.clientY, origX: x, origY: y, origW: w, origH: h };
    const onMove = (ev) => {
      if (!resizing.current) return;
      const r = resizing.current;
      const dx = ev.clientX - r.startX;
      const dy = ev.clientY - r.startY;
      let nx = r.origX, ny = r.origY, nw = r.origW, nh = r.origH;
      if (handle.includes('e')) nw = Math.max(20, r.origW + dx);
      if (handle.includes('s')) nh = Math.max(10, r.origH + dy);
      if (handle.includes('w')) { nw = Math.max(20, r.origW - dx); nx = r.origX + (r.origW - nw); }
      if (handle.includes('n')) { nh = Math.max(10, r.origH - dy); ny = r.origY + (r.origH - nh); }
      // clamp inside canvas
      if (nx < 0) { nw += nx; nx = 0; }
      if (ny < 0) { nh += ny; ny = 0; }
      if (nx + nw > CANVAS_W) nw = CANVAS_W - nx;
      if (ny + nh > CANVAS_H) nh = CANVAS_H - ny;
      boxRef.current.style.left = nx + 'px';
      boxRef.current.style.top = ny + 'px';
      boxRef.current.style.width = nw + 'px';
      boxRef.current.style.height = nh + 'px';
    };
    const onUp = (ev) => {
      document.removeEventListener('pointermove', onMove);
      document.removeEventListener('pointerup', onUp);
      if (!resizing.current) return;
      const r = resizing.current;
      const dx = ev.clientX - r.startX;
      const dy = ev.clientY - r.startY;
      let nx = r.origX, ny = r.origY, nw = r.origW, nh = r.origH;
      if (handle.includes('e')) nw = Math.max(20, r.origW + dx);
      if (handle.includes('s')) nh = Math.max(10, r.origH + dy);
      if (handle.includes('w')) { nw = Math.max(20, r.origW - dx); nx = r.origX + (r.origW - nw); }
      if (handle.includes('n')) { nh = Math.max(10, r.origH - dy); ny = r.origY + (r.origH - nh); }
      if (nx < 0) { nw += nx; nx = 0; }
      if (ny < 0) { nh += ny; ny = 0; }
      if (nx + nw > CANVAS_W) nw = CANVAS_W - nx;
      if (ny + nh > CANVAS_H) nh = CANVAS_H - ny;
      resizing.current = null;
      onResizeStop(Math.round(nw), Math.round(nh), Math.round(nx), Math.round(ny));
    };
    document.addEventListener('pointermove', onMove);
    document.addEventListener('pointerup', onUp);
  }, [x, y, w, h, onResizeStop]);

  const handleStyle = (cursor) => ({
    position: 'absolute', zIndex: 2,
    width: cursor === 'ew-resize' ? 6 : cursor === 'ns-resize' ? '100%' : 8,
    height: cursor === 'ns-resize' ? 6 : cursor === 'ew-resize' ? '100%' : 8,
    cursor,
    ...(cursor === 'nwse-resize' ? { top: -3, left: -3 } : {}),
    ...(cursor === 'nesw-resize' && { top: -3, right: -3 } || {}),
    ...(cursor === 'ns-resize' ? { left: 0, bottom: -3 } : {}),
    ...(cursor === 'ew-resize' ? { top: 0, right: -3 } : {}),
  });

  const handles = [
    { key: 'se', cursor: 'nwse-resize', style: { position:'absolute', bottom:-3, right:-3, width:8, height:8, cursor:'nwse-resize', zIndex:2 } },
    { key: 'sw', cursor: 'nesw-resize', style: { position:'absolute', bottom:-3, left:-3, width:8, height:8, cursor:'nesw-resize', zIndex:2 } },
    { key: 'ne', cursor: 'nesw-resize', style: { position:'absolute', top:-3, right:-3, width:8, height:8, cursor:'nesw-resize', zIndex:2 } },
    { key: 'nw', cursor: 'nwse-resize', style: { position:'absolute', top:-3, left:-3, width:8, height:8, cursor:'nwse-resize', zIndex:2 } },
    { key: 'e',  cursor: 'ew-resize',   style: { position:'absolute', top:0, right:-3, width:6, height:'100%', cursor:'ew-resize', zIndex:2 } },
    { key: 'w',  cursor: 'ew-resize',   style: { position:'absolute', top:0, left:-3, width:6, height:'100%', cursor:'ew-resize', zIndex:2 } },
    { key: 's',  cursor: 'ns-resize',   style: { position:'absolute', bottom:-3, left:0, width:'100%', height:6, cursor:'ns-resize', zIndex:2 } },
    { key: 'n',  cursor: 'ns-resize',   style: { position:'absolute', top:-3, left:0, width:'100%', height:6, cursor:'ns-resize', zIndex:2 } },
  ];

  return (
    <div
      ref={boxRef}
      onPointerDown={onPointerDownDrag}
      style={{
        position: 'absolute', left: x, top: y, width: w, height: h,
        cursor: 'move', touchAction: 'none',
        boxShadow: selected ? '0 0 0 2px #60a5fa, 0 0 0 4px rgba(96,165,250,0.25)' : undefined,
        ...extraStyle,
      }}
      className={!selected ? 'hover:shadow-[0_0_0_1px_rgba(96,165,250,0.4)]' : undefined}
    >
      {children}
      {enableResize && selected && handles.map(({ key, style: hs }) => (
        <div key={key} data-handle="1" style={hs} onPointerDown={(e) => onPointerDownResize(e, key)} />
      ))}
    </div>
  );
}

const defaultElement = (type, overrides = {}) => {
  const base = {
    id: Date.now().toString() + Math.random().toString(36).slice(2, 6),
    type, // text, image, shape, divider
    x: 50, y: 50, w: 200, h: 40,
    content: '',
    variable: '', // {{name}}, {{course}}, {{date}}, {{cert_number}}, {{instructor}}
    fontSize: 16,
    fontFamily: 'sans-serif',
    fontWeight: 'normal',
    fontStyle: 'normal',
    color: '#ffffff',
    textAlign: 'center',
    opacity: 1,
    letterSpacing: 0,
    textTransform: 'none', // none, uppercase
    ...overrides
  };
  return base;
};

const variableOptions = [
  { value: '__static__', label: 'Teks Statis' },
  { value: '{{name}}', label: 'Nama Penerima' },
  { value: '{{course}}', label: 'Nama Kursus' },
  { value: '{{date}}', label: 'Tanggal Selesai' },
  { value: '{{cert_number}}', label: 'No. Sertifikat' },
  { value: '{{instructor}}', label: 'Instruktur' },
];

const presetElements = [
  { label: 'Judul "Certificate"', el: { type: 'text', content: 'CERTIFICATE OF COMPLETION', variable: '__static__', x: 150, y: 60, w: 500, h: 40, fontSize: 24, fontWeight: 'bold', color: '#c9a227', textAlign: 'center', letterSpacing: 6, textTransform: 'uppercase' }},
  { label: 'Nama Penerima', el: { type: 'text', content: '', variable: '{{name}}', x: 150, y: 200, w: 500, h: 55, fontSize: 38, fontFamily: 'Georgia, serif', color: '#ffffff', textAlign: 'center' }},
  { label: 'Nama Kursus', el: { type: 'text', content: '', variable: '{{course}}', x: 150, y: 290, w: 500, h: 40, fontSize: 22, fontWeight: 'bold', color: '#c9a227', textAlign: 'center' }},
  { label: 'Label "This is to certify that"', el: { type: 'text', content: 'This is to certify that', variable: '__static__', x: 200, y: 170, w: 400, h: 25, fontSize: 12, color: '#ffffff', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 3, opacity: 0.7 }},
  { label: 'Label "has successfully completed"', el: { type: 'text', content: 'has successfully completed', variable: '__static__', x: 200, y: 260, w: 400, h: 25, fontSize: 12, color: '#ffffff', textAlign: 'center', textTransform: 'uppercase', letterSpacing: 3, opacity: 0.7 }},
  { label: 'Tanggal', el: { type: 'text', content: '', variable: '{{date}}', x: 250, y: 350, w: 300, h: 25, fontSize: 13, color: '#ffffff', textAlign: 'center', opacity: 0.7 }},
  { label: 'No. Sertifikat', el: { type: 'text', content: '', variable: '{{cert_number}}', x: 250, y: 500, w: 300, h: 20, fontSize: 10, color: '#ffffff', textAlign: 'center', opacity: 0.5 }},
  { label: 'Instruktur / Penandatangan', el: { type: 'text', content: '', variable: '{{instructor}}', x: 280, y: 430, w: 240, h: 30, fontSize: 14, fontWeight: 'bold', color: '#ffffff', textAlign: 'center' }},
  { label: 'Garis Divider', el: { type: 'divider', x: 300, y: 150, w: 200, h: 4, color: '#c9a227' }},
  { label: 'Garis Tanda Tangan', el: { type: 'divider', x: 300, y: 420, w: 200, h: 2, color: '#ffffff', opacity: 0.3 }},
  { label: 'Icon Award ⭐', el: { type: 'icon', content: 'award', x: 370, y: 10, w: 60, h: 60, color: '#c9a227' }},
];

const defaultSettings = {
  enabled: true,
  background_color: '#1e3a5f',
  border_style: 'ornate',
  border_color: '#c9a227',
  custom_elements: []
};

export default function TenantCertificateSettings() {
  const [settings, setSettings] = useState(defaultSettings);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, certsRes] = await Promise.all([
          api.get('/tenant/certificate-settings'),
          api.get('/tenant/certificates')
        ]);
        const s = settingsRes.data || {};
        // Migrate old format: if custom_elements is empty but old fields exist, auto-fill
        if (!s.custom_elements || s.custom_elements.length === 0) {
          setSettings({ ...defaultSettings, ...s, custom_elements: [] });
        } else {
          setSettings({ ...defaultSettings, ...s });
        }
        setCertificates(certsRes.data.certificates || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/tenant/certificate-settings', settings);
      toast.success('Desain sertifikat disimpan!');
    } catch (e) {
      toast.error('Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const addElement = (preset) => {
    const { id: _ignore, type, ...rest } = preset;
    const el = defaultElement(type, rest);
    setSettings(prev => ({ ...prev, custom_elements: [...prev.custom_elements, el] }));
    setSelectedId(el.id);
  };

  const updateElement = (id, updates) => {
    setSettings(prev => ({
      ...prev,
      custom_elements: prev.custom_elements.map(el => el.id === id ? { ...el, ...updates } : el)
    }));
  };

  const deleteElement = (id) => {
    setSettings(prev => ({
      ...prev,
      custom_elements: prev.custom_elements.filter(el => el.id !== id)
    }));
    if (selectedId === id) setSelectedId(null);
  };

  const duplicateElement = (id) => {
    const el = settings.custom_elements.find(e => e.id === id);
    if (!el) return;
    const newEl = { ...el, id: Date.now().toString() + Math.random().toString(36).slice(2, 6), x: el.x + 20, y: el.y + 20 };
    setSettings(prev => ({ ...prev, custom_elements: [...prev.custom_elements, newEl] }));
    setSelectedId(newEl.id);
  };

  const moveLayer = (id, direction) => {
    setSettings(prev => {
      const els = [...prev.custom_elements];
      const idx = els.findIndex(e => e.id === id);
      if (idx < 0) return prev;
      const swapIdx = direction === 'up' ? idx + 1 : idx - 1;
      if (swapIdx < 0 || swapIdx >= els.length) return prev;
      [els[idx], els[swapIdx]] = [els[swapIdx], els[idx]];
      return { ...prev, custom_elements: els };
    });
  };

  const selectedEl = settings.custom_elements.find(e => e.id === selectedId);

  const handleUploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await api.post('/tenant/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = res.data.url;
      const el = defaultElement('image', { content: url, x: 350, y: 20, w: 100, h: 80 });
      setSettings(prev => ({ ...prev, custom_elements: [...prev.custom_elements, el] }));
      setSelectedId(el.id);
      toast.success('Logo diupload!');
    } catch (err) {
      toast.error('Gagal upload');
    }
  };

  const renderElementPreview = (el) => {
    const v = el.variable && el.variable !== '__static__' ? el.variable : '';
    const displayText = v
      ? v === '{{name}}' ? 'John Doe'
        : v === '{{course}}' ? 'Nama Kursus'
        : v === '{{date}}' ? '9 Maret 2026'
        : v === '{{cert_number}}' ? 'CERT-XXXXXXXX'
        : v === '{{instructor}}' ? 'Nama Instruktur'
        : el.content
      : el.content;

    if (el.type === 'divider') {
      return <div style={{ width: '100%', height: '100%', backgroundColor: el.color, opacity: el.opacity ?? 1, borderRadius: 2 }} />;
    }
    if (el.type === 'icon' && el.content === 'award') {
      return <Award style={{ width: '100%', height: '100%', color: el.color, opacity: el.opacity ?? 1 }} />;
    }
    if (el.type === 'image') {
      return <img src={el.content} alt="" style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: el.opacity ?? 1 }} />;
    }
    // text
    return (
      <div style={{
        width: '100%', height: '100%', display: 'flex', alignItems: 'center',
        justifyContent: el.textAlign === 'left' ? 'flex-start' : el.textAlign === 'right' ? 'flex-end' : 'center',
        fontSize: el.fontSize, fontFamily: el.fontFamily || 'sans-serif',
        fontWeight: el.fontWeight || 'normal', fontStyle: el.fontStyle || 'normal',
        color: el.color, textAlign: el.textAlign || 'center', opacity: el.opacity ?? 1,
        letterSpacing: el.letterSpacing || 0, textTransform: el.textTransform || 'none',
        overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', lineHeight: 1.2,
        userSelect: 'none',
      }}>
        {displayText || '(kosong)'}
      </div>
    );
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>;

  return (
    <div data-testid="certificate-settings-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Desain Sertifikat</h1>
          <p className="text-sm text-slate-500">Drag & drop untuk mendesain sertifikat kursus</p>
        </div>
        <Button onClick={handleSave} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" disabled={saving} data-testid="save-cert-settings-btn">
          {saving && <Loader2 size={14} className="animate-spin mr-2" />}
          <Save size={14} className="mr-2" /> Simpan Desain
        </Button>
      </div>

      <Tabs defaultValue="design">
        <TabsList>
          <TabsTrigger value="design" className="gap-2"><Palette size={14} /> Designer</TabsTrigger>
          <TabsTrigger value="issued" className="gap-2"><List size={14} /> Issued ({certificates.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="design" className="mt-6">
          <div className="grid grid-cols-1 xl:grid-cols-[280px_1fr_280px] gap-4">

            {/* Left Panel - Add Elements */}
            <div className="space-y-4 order-2 xl:order-1">
              {/* Background */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Background</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <input type="color" value={settings.background_color} onChange={e => setSettings({ ...settings, background_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                    <Input value={settings.background_color} onChange={e => setSettings({ ...settings, background_color: e.target.value })} className="flex-1 text-xs h-8" />
                  </div>
                  <div>
                    <Label className="text-xs">Border</Label>
                    <Select value={settings.border_style} onValueChange={v => setSettings({ ...settings, border_style: v })}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="ornate">Ornate</SelectItem>
                        <SelectItem value="simple">Simple</SelectItem>
                        <SelectItem value="none">None</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {settings.border_style !== 'none' && (
                    <div className="flex items-center gap-2">
                      <Label className="text-xs whitespace-nowrap">Border Color</Label>
                      <input type="color" value={settings.border_color || '#c9a227'} onChange={e => setSettings({ ...settings, border_color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Preset Elements */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm flex items-center gap-2"><Plus size={14} /> Tambah Elemen</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-1.5 max-h-[400px] overflow-y-auto">
                    {presetElements.map((p, i) => (
                      <button
                        key={i}
                        onClick={() => addElement(p.el)}
                        className="w-full text-left text-xs px-3 py-2 rounded-md hover:bg-slate-100 border border-slate-100 hover:border-slate-200 transition-colors flex items-center gap-2"
                      >
                        {p.el.type === 'text' ? <Type size={12} className="text-slate-400" /> :
                         p.el.type === 'divider' ? <div className="w-3 h-0.5 bg-slate-400" /> :
                         p.el.type === 'icon' ? <Award size={12} className="text-slate-400" /> :
                         <Image size={12} className="text-slate-400" />}
                        {p.label}
                      </button>
                    ))}
                    <label className="w-full text-left text-xs px-3 py-2 rounded-md hover:bg-slate-100 border border-slate-100 hover:border-slate-200 transition-colors flex items-center gap-2 cursor-pointer">
                      <Upload size={12} className="text-slate-400" /> Upload Logo / Gambar
                      <input type="file" accept="image/*" className="hidden" onChange={handleUploadLogo} />
                    </label>
                  </div>
                </CardContent>
              </Card>

              {/* Layers */}
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm">Layers ({settings.custom_elements.length})</CardTitle>
                </CardHeader>
                <CardContent className="px-4 pb-4">
                  <div className="space-y-1 max-h-[200px] overflow-y-auto">
                    {settings.custom_elements.map((el, i) => (
                      <div
                        key={el.id}
                        onClick={() => setSelectedId(el.id)}
                        className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded cursor-pointer ${selectedId === el.id ? 'bg-blue-50 border border-blue-200' : 'hover:bg-slate-50 border border-transparent'}`}
                      >
                        <GripVertical size={10} className="text-slate-300" />
                        <span className="flex-1 truncate">
                          {el.type === 'text' ? ((el.variable && el.variable !== '__static__') ? variableOptions.find(v => v.value === el.variable)?.label : el.content?.slice(0, 20)) || '(teks)' :
                           el.type === 'divider' ? 'Garis' :
                           el.type === 'icon' ? 'Icon' : 'Gambar'}
                        </span>
                        <button onClick={e => { e.stopPropagation(); deleteElement(el.id); }} className="text-slate-300 hover:text-red-500">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {!settings.custom_elements.length && <p className="text-xs text-slate-400 text-center py-4">Belum ada elemen</p>}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Center - Canvas */}
            <div className="order-1 xl:order-2">
              <Card className="border-slate-200 shadow-sm">
                <CardHeader className="py-3 px-4">
                  <CardTitle className="text-sm flex items-center gap-2"><Eye size={14} /> Canvas Preview</CardTitle>
                </CardHeader>
                <CardContent className="p-4">
                  <div className="overflow-auto">
                    <div
                      ref={canvasRef}
                      className="relative mx-auto border border-slate-300 rounded-lg overflow-hidden"
                      style={{
                        width: CANVAS_W, height: CANVAS_H,
                        backgroundColor: settings.background_color,
                        minWidth: CANVAS_W,
                      }}
                      onClick={(e) => { if (e.target === e.currentTarget || e.target === canvasRef.current) setSelectedId(null); }}
                    >
                      {/* Border Ornaments */}
                      {settings.border_style === 'ornate' && (
                        <>
                          <div className="absolute top-3 left-3 w-16 h-16 opacity-30 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227', borderWidth: '2px', borderStyle: 'solid', borderRight: 'none', borderBottom: 'none' }} />
                          <div className="absolute top-3 right-3 w-16 h-16 opacity-30 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227', borderWidth: '2px', borderStyle: 'solid', borderLeft: 'none', borderBottom: 'none' }} />
                          <div className="absolute bottom-3 left-3 w-16 h-16 opacity-30 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227', borderWidth: '2px', borderStyle: 'solid', borderRight: 'none', borderTop: 'none' }} />
                          <div className="absolute bottom-3 right-3 w-16 h-16 opacity-30 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227', borderWidth: '2px', borderStyle: 'solid', borderLeft: 'none', borderTop: 'none' }} />
                          <div className="absolute inset-6 border opacity-20 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227' }} />
                        </>
                      )}
                      {settings.border_style === 'simple' && (
                        <div className="absolute inset-4 border-2 pointer-events-none" style={{ borderColor: settings.border_color || '#c9a227' }} />
                      )}

                      {/* Draggable Elements */}
                      {settings.custom_elements.map(el => (
                        <DraggableBox
                          key={el.id}
                          x={el.x} y={el.y} w={el.w} h={el.h}
                          selected={selectedId === el.id}
                          onSelect={() => setSelectedId(el.id)}
                          onDragStop={(nx, ny) => updateElement(el.id, { x: nx, y: ny })}
                          onResizeStop={(nw, nh, nx, ny) => updateElement(el.id, { w: nw, h: nh, x: nx, y: ny })}
                          enableResize={selectedId === el.id}
                          style={{ zIndex: selectedId === el.id ? 50 : 10 }}
                        >
                          {renderElementPreview(el)}
                        </DraggableBox>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Right Panel - Element Properties */}
            <div className="order-3">
              {selectedEl ? (
                <Card className="border-slate-200 shadow-sm">
                  <CardHeader className="py-3 px-4 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm">Properti Elemen</CardTitle>
                    <div className="flex gap-1">
                      <button onClick={() => duplicateElement(selectedEl.id)} className="p-1 hover:bg-slate-100 rounded" title="Duplikat">
                        <Copy size={14} className="text-slate-400" />
                      </button>
                      <button onClick={() => deleteElement(selectedEl.id)} className="p-1 hover:bg-red-50 rounded" title="Hapus">
                        <Trash2 size={14} className="text-red-400" />
                      </button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-4 pb-4 space-y-3">
                    {/* Variable / Content */}
                    {(selectedEl.type === 'text') && (
                      <>
                        <div>
                          <Label className="text-xs">Konten</Label>
                          <Select value={selectedEl.variable || '__static__'} onValueChange={v => updateElement(selectedEl.id, { variable: v === '__static__' ? '' : v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Teks Statis" /></SelectTrigger>
                            <SelectContent>
                              {variableOptions.map(v => <SelectItem key={v.value} value={v.value}>{v.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        {(!selectedEl.variable || selectedEl.variable === '__static__') && (
                          <div>
                            <Label className="text-xs">Teks</Label>
                            <Input value={selectedEl.content} onChange={e => updateElement(selectedEl.id, { content: e.target.value })} className="h-8 text-xs" placeholder="Ketik teks..." />
                          </div>
                        )}
                      </>
                    )}

                    {selectedEl.type === 'image' && (
                      <div>
                        <Label className="text-xs">URL Gambar</Label>
                        <Input value={selectedEl.content} onChange={e => updateElement(selectedEl.id, { content: e.target.value })} className="h-8 text-xs" placeholder="https://..." />
                      </div>
                    )}

                    {/* Position */}
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-xs">X</Label>
                        <Input type="number" value={Math.round(selectedEl.x)} onChange={e => updateElement(selectedEl.id, { x: +e.target.value })} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">Y</Label>
                        <Input type="number" value={Math.round(selectedEl.y)} onChange={e => updateElement(selectedEl.id, { y: +e.target.value })} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">Width</Label>
                        <Input type="number" value={Math.round(selectedEl.w)} onChange={e => updateElement(selectedEl.id, { w: +e.target.value })} className="h-8 text-xs" />
                      </div>
                      <div>
                        <Label className="text-xs">Height</Label>
                        <Input type="number" value={Math.round(selectedEl.h)} onChange={e => updateElement(selectedEl.id, { h: +e.target.value })} className="h-8 text-xs" />
                      </div>
                    </div>

                    {/* Typography (for text type) */}
                    {selectedEl.type === 'text' && (
                      <>
                        <div>
                          <Label className="text-xs">Font Size</Label>
                          <Input type="number" value={selectedEl.fontSize} onChange={e => updateElement(selectedEl.id, { fontSize: +e.target.value })} className="h-8 text-xs" min={8} max={72} />
                        </div>
                        <div>
                          <Label className="text-xs">Font</Label>
                          <Select value={selectedEl.fontFamily || 'sans-serif'} onValueChange={v => updateElement(selectedEl.id, { fontFamily: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sans-serif">Sans Serif</SelectItem>
                              <SelectItem value="Georgia, serif">Serif (Georgia)</SelectItem>
                              <SelectItem value="monospace">Monospace</SelectItem>
                              <SelectItem value="'Plus Jakarta Sans', sans-serif">Plus Jakarta Sans</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => updateElement(selectedEl.id, { fontWeight: selectedEl.fontWeight === 'bold' ? 'normal' : 'bold' })} className={`p-1.5 rounded border ${selectedEl.fontWeight === 'bold' ? 'bg-slate-200 border-slate-300' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <Bold size={14} />
                          </button>
                          <button onClick={() => updateElement(selectedEl.id, { fontStyle: selectedEl.fontStyle === 'italic' ? 'normal' : 'italic' })} className={`p-1.5 rounded border ${selectedEl.fontStyle === 'italic' ? 'bg-slate-200 border-slate-300' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <Italic size={14} />
                          </button>
                          <div className="border-l border-slate-200 mx-1" />
                          <button onClick={() => updateElement(selectedEl.id, { textAlign: 'left' })} className={`p-1.5 rounded border ${selectedEl.textAlign === 'left' ? 'bg-slate-200 border-slate-300' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <AlignLeft size={14} />
                          </button>
                          <button onClick={() => updateElement(selectedEl.id, { textAlign: 'center' })} className={`p-1.5 rounded border ${selectedEl.textAlign === 'center' ? 'bg-slate-200 border-slate-300' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <AlignCenter size={14} />
                          </button>
                          <button onClick={() => updateElement(selectedEl.id, { textAlign: 'right' })} className={`p-1.5 rounded border ${selectedEl.textAlign === 'right' ? 'bg-slate-200 border-slate-300' : 'border-slate-200 hover:bg-slate-50'}`}>
                            <AlignRight size={14} />
                          </button>
                        </div>
                        <div>
                          <Label className="text-xs">Letter Spacing</Label>
                          <Input type="number" value={selectedEl.letterSpacing || 0} onChange={e => updateElement(selectedEl.id, { letterSpacing: +e.target.value })} className="h-8 text-xs" min={0} max={20} />
                        </div>
                        <div>
                          <Label className="text-xs">Transform</Label>
                          <Select value={selectedEl.textTransform || 'none'} onValueChange={v => updateElement(selectedEl.id, { textTransform: v })}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Normal</SelectItem>
                              <SelectItem value="uppercase">UPPERCASE</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </>
                    )}

                    {/* Color */}
                    <div>
                      <Label className="text-xs">Warna</Label>
                      <div className="flex items-center gap-2">
                        <input type="color" value={selectedEl.color} onChange={e => updateElement(selectedEl.id, { color: e.target.value })} className="w-8 h-8 rounded cursor-pointer border-0" />
                        <Input value={selectedEl.color} onChange={e => updateElement(selectedEl.id, { color: e.target.value })} className="flex-1 h-8 text-xs" />
                      </div>
                    </div>

                    {/* Opacity */}
                    <div>
                      <Label className="text-xs">Opacity</Label>
                      <Input type="number" value={selectedEl.opacity ?? 1} onChange={e => updateElement(selectedEl.id, { opacity: +e.target.value })} className="h-8 text-xs" min={0} max={1} step={0.1} />
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-slate-200 shadow-sm">
                  <CardContent className="py-12 text-center">
                    <Type size={32} className="mx-auto text-slate-200 mb-3" />
                    <p className="text-sm text-slate-400">Klik elemen di canvas untuk edit propertinya</p>
                    <p className="text-xs text-slate-300 mt-1">Atau tambah elemen baru dari panel kiri</p>
                  </CardContent>
                </Card>
              )}

              {/* Enable Toggle */}
              <Card className="border-slate-200 shadow-sm mt-4">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-900">Auto Generate</p>
                      <p className="text-xs text-slate-500">Sertifikat otomatis saat kursus selesai</p>
                    </div>
                    <Switch checked={settings.enabled} onCheckedChange={v => setSettings({ ...settings, enabled: v })} data-testid="cert-enabled-switch" />
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="issued" className="mt-6">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-slate-100 bg-slate-50">
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Penerima</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Kursus</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">No. Sertifikat</th>
                  <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                </tr></thead>
                <tbody>
                  {certificates.map((c, i) => (
                    <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium text-slate-900">{c.user_name}</p>
                        <p className="text-xs text-slate-500">{c.user_email}</p>
                      </td>
                      <td className="py-3 px-4 text-slate-600">{c.course_title}</td>
                      <td className="py-3 px-4 font-mono text-xs text-slate-600">{c.certificate_number}</td>
                      <td className="py-3 px-4 text-slate-500">{new Date(c.completed_at).toLocaleDateString('id-ID')}</td>
                    </tr>
                  ))}
                  {!certificates.length && <tr><td colSpan={4} className="py-12 text-center text-slate-400">Belum ada sertifikat</td></tr>}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
